/* ============================================================
   ILS — Full cart page (TASK-2153).

   Wires the page-scoped equivalents of the cart drawer's
   interactions on templates/cart.json:
     - remove-with-confirmation modal for cart lines
     - qty stepper (+/- and direct input) via /cart/change.js
     - upsell strip: initial paint + add-to-cart + weight popup
     - continue shopping button
     - checkout button (form submit)
     - leader row hydration + confetti when the page cart crosses
       the weekly leader (mirrors the drawer's behaviour scoped to
       page-scoped data attributes so drawer + page don't collide)
     - re-fetch + morph of this section after any cart mutation, so
       rewards / stim / pop repaint against the new totals.

   Rewards + stim/pop panels use the drawer's own data attributes
   (`[data-ils-rewards]`, `[data-ils-cd-stim]`, `[data-ils-cd-pop]`);
   custom-cart-drawer.js selects those globally with
   querySelectorAll, so the page copy is picked up automatically —
   no duplication required.
   ============================================================ */
(function () {
  'use strict';

  var PAGE_SELECTOR = '[data-ils-cart-page]';

  function root() { return document.querySelector(PAGE_SELECTOR); }

  function localeRoot() {
    try {
      var r = window.Shopify && Shopify.routes && Shopify.routes.root;
      if (typeof r === 'string' && r.length > 0) {
        var trimmed = r.replace(/\/+$/, '');
        return trimmed === '' ? '' : trimmed;
      }
    } catch (e) {}
    return '';
  }

  function moneyFromCents(cents) {
    /* TASK-5795 — PRESENTMENT minor units, printed in the market's own format.
       Mirrors the same change in custom-cart-drawer.js; null from the oracle
       falls through to the legacy euro body, so EUR markets do not move. */
    if (window.ILSMoney && window.ILSMoney.on()) {
      var _p5795 = window.ILSMoney.fromCents(cents);
      if (_p5795 !== null) return _p5795;
    }
    var sign = cents < 0 ? '-' : '';
    var abs = Math.abs(cents | 0);
    var euros = Math.floor(abs / 100);
    var c = String(abs % 100).padStart(2, '0');
    var eFormatted = String(euros).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    /* TASK-5946 — display only: a FULLY zero fraction prints as no fraction. */
    return sign + '€' + eFormatted + (c === '00' ? '' : ',' + c);
  }

  // TASK-2567: localized "Save €X" label for the cart-page WE RECOMMEND upsell.
  // Reads the translated `custom.product.save_amount` template from the upsell
  // host (data-save-tpl, money slot = brace-free sentinel) and substitutes the
  // amount as PLAIN TEXT so it cannot leak HTML (TASK-2565 bug class). Falls
  // back to English "Save €X" when the host/attr is absent.
  function upsellSaveLabel(diffCents) {
    var amount = moneyFromCents(diffCents);
    var host = document.querySelector('[data-ils-cp-upsell]');
    var tpl = host && host.getAttribute('data-save-tpl');
    if (tpl && /__AMOUNT__|\{\{\s*amount\s*\}\}/.test(tpl)) {
      return tpl.replace(/__AMOUNT__|\{\{\s*amount\s*\}\}/g, amount);
    }
    return 'Save ' + amount;
  }

  /* TASK-4846 — ONE global money format on EVERY published locale,
     English included: decimal separator ',' and thousands separator '.'
     (€0,00 / €89,90 / €1.234,56). Deliberately not Intl.NumberFormat or
     toLocaleString: this is a storefront business rule, not a locale
     convention, and en-* would render €1,234.56. Same algorithm as
     moneyFromCents() in custom-cart-drawer.js. Presentation only — the
     numeric value and its rounding are untouched. */
  function money2(n) {
    const v = isFinite(Number(n)) ? Number(n) : 0;
    const sign = v < 0 ? '-' : '';
    const cents = Math.round(Math.abs(v) * 100);
    /* TASK-5946 — display only: a FULLY zero fraction prints as no fraction. */
    const frac = String(cents % 100).padStart(2, '0');
    return sign + String(Math.floor(cents / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, '.') +
      (frac === '00' ? '' : ',' + frac);
  }

  function formatWeight(rawValue) {
    if (window.ILS && typeof window.ILS.formatWeight === 'function') {
      return window.ILS.formatWeight(rawValue);
    }
    var src = (rawValue == null ? '' : String(rawValue));
    var m = src.match(/\d+/);
    if (!m) return src;
    var grams = parseInt(m[0], 10);
    if (/kg|кг/i.test(src)) grams *= 1000; /* TASK-2632: cyrillic "кг" on ru markets */
    /* TASK-2632: match ils-weight — cyrillic units on ru, latin elsewhere. */
    var lang = String((window.Shopify && Shopify.locale) || (document.documentElement && document.documentElement.lang) || '').toLowerCase();
    var isRu = lang.indexOf('ru') === 0;
    var uG = isRu ? 'г' : 'g';
    var uKg = isRu ? 'кг' : 'kg';
    if (grams < 1000) return grams + ' ' + uG;
    var kgInt = Math.floor(grams / 1000);
    var rem = grams % 1000;
    if (rem === 0) return kgInt + ' ' + uKg;
    var frac = String(rem).padStart(3, '0').replace(/0+$/, '');
    return kgInt + ',' + frac + ' ' + uKg;
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  /* TASK-2603 — read the localized "Select weight" from the data-ils-rw-i18n
     JSON block emitted by snippets/custom-cart-rewards.liquid (rendered on the
     cart page via custom-main-cart). English fallback if the block is absent. */
  var _cpSelWeightCache = null;
  function cpSelectWeight() {
    if (_cpSelWeightCache) return _cpSelWeightCache;
    var v = 'Select weight';
    try {
      var el = document.querySelector('[data-ils-rw-i18n]');
      if (el && el.textContent) {
        var p = JSON.parse(el.textContent);
        if (p && p.selectWeight) { v = p.selectWeight; _cpSelWeightCache = v; }
      }
    } catch (e) {}
    return v;
  }

  /* ---------- Cart mutation + section morph ---------- */

  function sectionId() {
    var r = root();
    return r ? (r.getAttribute('data-section-id') || '') : '';
  }

  function refreshDrawerSection(sectionsPayload) {
    // Let the header cart icon + drawer subscribers know the cart mutated;
    // Dawn publishes 'cart-update' on PUB_SUB_EVENTS, and the drawer's
    // custom-cart-drawer.js listens via subscribe(PUB_SUB_EVENTS.cartUpdate).
    // We manually dispatch a legacy 'cart:update' event too — the drawer
    // JS listens for that as well.
    /* TASK-3064 — mark this as an INTERNAL ping so our own cart:update listener
       (init) doesn't treat it as an external mutation and fire a redundant
       fetchAndMorphSelf() — cartChangeByKey / cartAddVariant already morph once.
       The drawer's listener ignores the detail and still repaints. */
    try { document.dispatchEvent(new CustomEvent('cart:update', { detail: { ilsCpInternal: true } })); } catch (e) {}
    // Also refetch cart.js so the drawer's own icon-bubble + rewards fire.
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart) return;
        // Sync the drawer icon count etc.
        var els = document.querySelectorAll('[data-ils-cart-count], .ils-cart__count, [data-ils-cd-count-num]');
        els.forEach(function (el) { el.textContent = String(cart.item_count || 0); });
      })
      .catch(function () {});
  }

  function morphSelfSection(html) {
    if (!html) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var newRoot = doc.querySelector(PAGE_SELECTOR);
    var live = root();
    if (!newRoot || !live) return;

    // Preserve upsell strip subtree (its rendered cards + hydrated ratings)
    // exactly like custom-cart-drawer.js does for the drawer's upsell.
    var liveUpsell = live.querySelector('[data-ils-cp-upsell]');
    var liveUpsellList = liveUpsell && liveUpsell.querySelector('[data-ils-cp-upsell-list]');
    var preserveUpsell = liveUpsellList && liveUpsellList.children.length > 0 ? liveUpsell : null;
    if (preserveUpsell && preserveUpsell.parentNode) {
      preserveUpsell.parentNode.removeChild(preserveUpsell);
    }

    live.innerHTML = newRoot.innerHTML;

    var freshUpsell = live.querySelector('[data-ils-cp-upsell]');
    if (freshUpsell) {
      if (preserveUpsell && freshUpsell.parentNode) {
        freshUpsell.parentNode.replaceChild(preserveUpsell, freshUpsell);
      } else {
        freshUpsell.hidden = false;
      }
    }

    // Re-run page-specific init on the fresh DOM.
    postMorphInit();
  }

  function fetchAndMorphSelf() {
    /* Shopify's Section Rendering API (`?section_id=`) is inconsistent
       for cart-page sections in JSON templates (returns 404 in tests),
       so we fetch the full /cart page HTML and morph our subtree out of
       it. Cost is one extra HTTP round-trip vs a section fetch, but
       guarantees a working recovery path when /cart/change.js does not
       return our section HTML in its `sections` payload. */
    var url = (window.routes && routes.cart_url ? routes.cart_url : '/cart');
    return fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (html) morphSelfSection(html);
      })
      .catch(function () {});
  }

  function isCartOk(resp, data) {
    if (!resp || !resp.ok) return false;
    if (data && data.status && data.status !== 200) return false;
    if (data && data.errors) return false;
    return true;
  }

  function cartChangeByKey(lineKey, quantity) {
    var body = {
      id: lineKey,
      quantity: quantity,
      sections: [sectionId(), 'cart-drawer', 'cart-icon-bubble'].filter(Boolean)
    };
    /* TASK-2962 — prefix the mutation endpoint with the active market/locale
       root (localeRoot()). Shopify's Section Rendering API renders the returned
       `sections` HTML in the locale of the REQUEST path; a naked `/cart/change.js`
       resolves to the store's DEFAULT locale (de), so the re-rendered empty-cart
       state (and any line-item HTML) leaked German on non-default markets like
       ru-de. `localeRoot()` yields e.g. `/ru-de`, keeping the section in the
       current language. */
    return fetch(localeRoot() + '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json().then(function (data) { return { resp: r, data: data }; }); })
      .then(function (out) {
        if (!isCartOk(out.resp, out.data)) {
          return fetchAndMorphSelf();
        }
        var sectionHtml = out.data && out.data.sections && out.data.sections[sectionId()];
        var morphPromise;
        if (sectionHtml) {
          morphSelfSection(sectionHtml);
          morphPromise = Promise.resolve();
        } else {
          /* Shopify's /cart/change.js does not always echo custom-template
             section HTML back (behaviour observed on this store); the full
             /cart fetch below is the deterministic recovery path. */
          morphPromise = fetchAndMorphSelf();
        }

        // Publish cart-update on Dawn's PUB_SUB so the drawer keeps in sync.
        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'ils-cart-page',
              productVariantId: '',
              cartData: out.data
            });
          }
        } catch (e) {}
        refreshDrawerSection(out.data && out.data.sections);
        return morphPromise.then(function () { return out.data; });
      })
      .catch(function () { return fetchAndMorphSelf(); });
  }

  function cartAddVariant(variantId, qty) {
    var body = {
      items: [{ id: parseInt(variantId, 10), quantity: qty || 1 }],
      sections: [sectionId(), 'cart-drawer', 'cart-icon-bubble'].filter(Boolean)
    };
    /* TASK-2962 — locale-prefix the add endpoint too, so the `sections`
       payload it returns is rendered in the active market locale (same class
       of leak as /cart/change.js). */
    return fetch(localeRoot() + '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json().then(function (data) { return { resp: r, data: data }; }); })
      .then(function (out) {
        if (out.data && out.data.status && out.data.status !== 200) return null;
        var sectionHtml = out.data && out.data.sections && out.data.sections[sectionId()];
        var morphPromise;
        if (sectionHtml) {
          morphSelfSection(sectionHtml);
          morphPromise = Promise.resolve();
        } else {
          morphPromise = fetchAndMorphSelf();
        }
        return morphPromise.then(function () {
          try {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'ils-cart-page-upsell',
                productVariantId: String(variantId),
                cartData: out.data
              });
            }
          } catch (e) {}
          refreshDrawerSection();
          return out.data;
        });
      })
      .catch(function () { return fetchAndMorphSelf(); });
  }

  function getLineKeyFromNode(node) {
    if (!node || !node.closest) return null;
    var item = node.closest('.item[data-line-key]');
    if (item && item.dataset.lineKey) return item.dataset.lineKey;
    var keyed = node.closest('[data-quantity-line-key]');
    if (keyed && keyed.dataset.quantityLineKey) return keyed.dataset.quantityLineKey;
    return null;
  }

  function isGiftLine(node) {
    if (!node) return false;
    var item = node.closest && node.closest('.item[data-is-gift], .item[data-line-index]');
    if (!item) return false;
    return item.getAttribute('data-is-gift') === 'true';
  }

  /* ---------- Line item events (remove, qty) ---------- */

  /* TASK-3064 — morph-safe remove-confirm wiring. The confirm modal
     [data-ils-cp-confirm] lives INSIDE [data-ils-cart-page], so every
     morphSelfSection() (`live.innerHTML = ...`) DESTROYS and recreates it.
     The previous version captured the modal node + wired its OK/Cancel buttons
     ONCE in init(); after the first remove-driven morph those closures pointed
     at a detached modal and the freshly-rendered modal had no handlers — so
     clicking remove on the next item ran openConfirm() on a dead node and
     nothing appeared (the "dead remove button" bug; only F5 fixed it). Fix:
     bind the .rm capture-listener + the modal controls ONCE to STABLE nodes
     (root() and document, neither replaced by morph) and resolve the modal
     FRESH via querySelector on every open/close. */
  var _cpPendingLineKey = null;
  function cpConfirmModal() { return document.querySelector('[data-ils-cp-confirm]'); }
  function cpOpenConfirm(lineKey) {
    var modal = cpConfirmModal();
    if (!modal) return;
    _cpPendingLineKey = lineKey;
    modal.hidden = false;
    modal.classList.add('on');
  }
  function cpCloseConfirm() {
    var modal = cpConfirmModal();
    if (modal) { modal.classList.remove('on'); modal.hidden = true; }
    _cpPendingLineKey = null;
  }

  function wireRemoveConfirm() {
    var r = root();
    if (!r) return;
    if (r.__ilsRemoveWired) return; // idempotent: root() survives morph, bind once
    r.__ilsRemoveWired = true;

    // Capture-phase so we win over Dawn's <cart-remove-button> click handler.
    r.addEventListener('click', function (e) {
      var rm = e.target.closest && e.target.closest('.rm');
      if (!rm) return;
      var item = rm.closest('.item[data-line-key], .item[data-line-index]');
      if (!item) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      var lineKey = (item.dataset && item.dataset.lineKey) || getLineKeyFromNode(rm);
      if (!lineKey) return;
      cpOpenConfirm(lineKey);
    }, true);

    // Modal controls via delegation on document — the modal node is recreated
    // by every morph, so we must NOT bind to specific button nodes.
    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      if (e.target.closest('[data-ils-cp-confirm-cancel]')) { cpCloseConfirm(); return; }
      if (e.target.closest('[data-ils-cp-confirm-ok]')) {
        var key = _cpPendingLineKey;
        cpCloseConfirm();
        if (key) cartChangeByKey(key, 0);
        return;
      }
      var modal = cpConfirmModal();
      if (modal && e.target === modal) cpCloseConfirm(); // backdrop click
    });
    document.addEventListener('keydown', function (e) {
      var modal = cpConfirmModal();
      if (e.key === 'Escape' && modal && modal.classList.contains('on')) cpCloseConfirm();
    });
  }

  function applyStepperMinusVisibility(scope) {
    if (!scope) return;
    scope.querySelectorAll('.ils-cd__items .stepper').forEach(function (stepper) {
      var input = stepper.querySelector('input.quantity__input, input.q');
      if (!input) return;
      var min = parseInt(input.getAttribute('min') || input.dataset.min || '1', 10) || 1;
      var v = parseInt(input.value, 10);
      if (!isFinite(v)) v = min;
      stepper.classList.toggle('is-min', v <= min);
    });
  }

  function wireStepperMinusVisibility() {
    var r = root();
    if (!r) return;
    applyStepperMinusVisibility(r);
    r.addEventListener('input', function (e) {
      if (e.target && e.target.matches && e.target.matches('input.quantity__input, input.q')) applyStepperMinusVisibility(r);
    }, true);
    r.addEventListener('change', function (e) {
      if (e.target && e.target.matches && e.target.matches('input.quantity__input, input.q')) applyStepperMinusVisibility(r);
    }, true);
    r.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var btn = e.target.closest('button[name="minus"], button[name="plus"]');
      if (!btn) return;
      setTimeout(function () { applyStepperMinusVisibility(r); }, 0);
    });
  }

  function wireKeyBasedQty() {
    var r = root();
    if (!r) return;

    r.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('button[name="minus"], button[name="plus"]');
      if (!btn) return;
      if (isGiftLine(btn)) return;
      var stepper = btn.closest('quantity-input, .stepper');
      if (!stepper) return;
      var input = stepper.querySelector('input[type="number"], .q');
      if (!input) return;
      var lineKey = getLineKeyFromNode(input) || getLineKeyFromNode(btn);
      if (!lineKey) return;

      var cur = parseInt((input.value || input.textContent || '1'), 10) || 1;
      var next = cur;
      if (btn.name === 'minus') next = Math.max(1, cur - 1);
      else if (btn.name === 'plus') next = cur + 1;
      if (next === cur) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      input.value = String(next);
      cartChangeByKey(lineKey, next);
    }, true);

    r.addEventListener('change', function (e) {
      var input = e.target.closest && e.target.closest('input.quantity__input, input.q');
      if (!input) return;
      if (isGiftLine(input)) return;
      var lineKey = getLineKeyFromNode(input);
      if (!lineKey) return;
      var next = parseInt(input.value || '1', 10);
      if (!isFinite(next) || next < 1) next = 1;
      input.value = String(next);

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      cartChangeByKey(lineKey, next);
    }, true);
  }

  /* ---------- Continue shopping + Checkout wire ---------- */

  function wireContinueShopping() {
    var r = root();
    if (!r) return;
    r.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('[data-ils-cp-continue]');
      if (!link) return;
      /* TASK-3060 — the EMPTY-cart "Continue shopping" CTA (.ils-cd__continue--empty)
         is a real <a> whose href is the intentional destination (all-products
         + ?sort_by=best-selling, set in TASK-3059). Honor that href verbatim: do
         NOT intercept — let the browser follow the anchor natively. Only the
         FILLED-cart footer control (.ils-cd__btn--continue, a <button> with no
         href) keeps the TASK-2961 "return to the page you came from" logic below. */
      if (link.classList && link.classList.contains('ils-cd__continue--empty')) return;
      e.preventDefault();
      /* TASK-2961 — return to the page the shopper opened the cart from.
         custom-cart-drawer.js records the last non-cart page it saw into
         sessionStorage.ILS_CONTINUE_URL on every page load. We read it back
         here and navigate there. Fallback (direct /cart entry, external
         referrer, storage blocked) → the shop home page via data-fallback-href. */
      var fallbackUrl = link.getAttribute('data-fallback-href') || '/';
      var target = '';
      try { target = sessionStorage.getItem('ILS_CONTINUE_URL') || ''; } catch (e2) {}
      // Only trust a same-origin RELATIVE path that isn't itself the cart.
      var pathOnly = target.split('?')[0].replace(/^\/[a-z]{2}-[a-z]{2}/, '');
      var safe = target.charAt(0) === '/' && target.charAt(1) !== '/' &&
                 pathOnly !== '/cart' && pathOnly.indexOf('/cart/') !== 0;
      var dest = safe ? target : fallbackUrl;
      setTimeout(function () { window.location.href = dest; }, 20);
    });
  }

  /* TASK-3340 — Checkout gate. The cart-page "Next" button is no longer a
     form-submit to /checkout (see custom-main-cart.liquid); it is a plain
     [data-ils-checkout-gate] button that we redirect to /pages/delivery-selector.
     The shopper picks country → courier/locker → operator → point there; that page
     writes the delivery_* cart attributes our carrier service needs and then hands
     off to /checkout. First iteration = ALWAYS route through the selector (simplest,
     safest); no "skip if already chosen" shortcut. Delegated on root() so it
     survives the page's section-morph re-renders, mirroring wireContinueShopping().
     The button is `disabled` (Liquid) when the cart is empty, so an empty cart can
     never reach the selector from here. */
  function wireCheckoutGate() {
    var r = root();
    if (!r) return;
    r.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-ils-checkout-gate]');
      if (!btn) return;
      e.preventDefault();
      if (btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
      btn.setAttribute('aria-busy', 'true');
      btn.classList.add('is-loading');
      /* TASK-5777 — the courier-only express handoff is offered the click
         first. On a single-country market root whose country is one of the
         courier-only destinations (23 of the 27 in practice — see the file
         header) it writes the delivery_* attributes and the _delivery_* line
         properties and goes straight to native checkout, and returns true to
         say it owns the navigation. Absent file, flag off, a Baltic country,
         a shared -eu root, an unreadable country or ANY failure -> false, and
         the TASK-3539 navigation below runs exactly as it did before. */
      try {
        var dexp = window.ILSDeliveryExpress;
        if (dexp && typeof dexp.handleGate === 'function' && dexp.handleGate(btn)) return;
      } catch (e5777) {}
      // TASK-3539: navigate straight to the LOCALIZED selector URL that Liquid
      // emits via custom-localize-url (pages['delivery-selector'].url carries the
      // translated handle, e.g. lv /pages/izvelieties-piegadi). The bare base handle
      // /pages/delivery-selector 301-redirects to it (~1s wasted); going direct
      // lands on 200. Falls back to the base handle when the attr is absent.
      var target = btn.getAttribute('data-ils-selector-url');
      window.location.href = (target && target.charAt(0) === '/')
        ? target
        : (localeRoot() + '/pages/delivery-selector');
    });
    // TASK-3539: bfcache reset for the gate. When the shopper reaches the selector
    // then taps browser BACK, /cart is restored from bfcache with this button still
    // aria-busy='true' + is-loading from the outgoing redirect, so the guard above
    // made the next click inert until a hard refresh. `pageshow` fires on a fresh
    // load AND a bfcache restore — clear the busy state on every gate button,
    // mirroring the selector's own Next reset (TASK-3220).
    window.addEventListener('pageshow', function () {
      var gates = document.querySelectorAll('[data-ils-checkout-gate]');
      for (var i = 0; i < gates.length; i++) {
        gates[i].removeAttribute('aria-busy');
        gates[i].classList.remove('is-loading');
      }
    });
  }

  /* ---------- Upsell (mirrors drawer's loadUpsell scoped to page) ---------- */

  var UPSELL_TARGET = 9;
  var upsellRunId = 0;
  var lastUpsellListHtml = '';

  var PLUS_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M11 4h2v7h7v2h-7v7h-2v-7H4v-2h7z"/></svg>';
  var STAR_PATH = 'M12 2.6c.55 0 1.04.33 1.27.83l2.3 4.96 5.43.7c.55.07.99.45 1.16.97.16.51-.01 1.07-.42 1.43l-4.02 3.52 1.06 5.4c.1.54-.12 1.08-.57 1.4-.45.31-1.04.34-1.52.07L12 18.86l-4.69 2.61c-.48.27-1.07.24-1.52-.07-.45-.32-.67-.86-.57-1.4l1.06-5.4-4.02-3.52c-.41-.36-.58-.92-.42-1.43.17-.52.61-.9 1.16-.97l5.43-.7 2.3-4.96c.23-.5.72-.83 1.27-.83z';
  function starSvg(state) {
    if (state === 'full') return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="' + STAR_PATH + '"/></svg>';
    if (state === 'dim') return '<svg viewBox="0 0 24 24" class="dim" aria-hidden="true"><path d="' + STAR_PATH + '"/></svg>';
    var gid = 'hg' + Math.random().toString(36).slice(2, 8);
    return '<svg viewBox="0 0 24 24" aria-hidden="true">'
      + '<defs><linearGradient id="' + gid + '"><stop offset="50%" stop-color="var(--star)"/><stop offset="50%" stop-color="var(--star-dim)"/></linearGradient></defs>'
      + '<path d="' + STAR_PATH + '" fill="url(#' + gid + ')"/></svg>';
  }
  function starsHtml(rate) {
    var html = '';
    for (var i = 1; i <= 5; i++) {
      if (rate >= i) html += starSvg('full');
      else if (rate >= i - .5) html += starSvg('half');
      else html += starSvg('dim');
    }
    return html;
  }

  function variantPriceCents(v) { return (v && v.price != null) ? Math.round(parseFloat(v.price)) : 0; }
  function variantCompareCents(v) {
    return (v && v.compare_at_price != null && parseFloat(v.compare_at_price) > parseFloat(v.price))
      ? Math.round(parseFloat(v.compare_at_price)) : 0;
  }

  function buildUpsellCard(product) {
    var url = product.url || '/products/' + (product.handle || '');
    var image = product.featured_image || (product.images && product.images[0]) || '';
    var imgSrc = image ? (typeof image === 'string' ? image : image.src || image) : '';
    var title = product.title || '';
    var variants = Array.isArray(product.variants) ? product.variants.filter(function (v) { return v && v.available !== false; }) : [];
    var allVariants = Array.isArray(product.variants) ? product.variants : [];
    var variant = variants[0] || allVariants[0] || null;
    if (!variant) return '';

    var opts = Array.isArray(product.options) ? product.options : [];
    var hasMultipleVariants = (opts.length === 1) && (allVariants.length > 1);
    var hasSingleVariant = (opts.length === 1) && (allVariants.length === 1);
    var hasWeightSel = hasMultipleVariants;

    var priceCents = variantPriceCents(variant);
    var compareCents = variantCompareCents(variant);
    var isSale = compareCents > 0;

    var priceHtml = isSale
      ? '<div class="price-col" data-up-price>'
        + '<span class="pr-old" data-up-old>' + moneyFromCents(compareCents) + '</span>'
        + '<span class="pr" data-up-now>' + moneyFromCents(priceCents) + '</span>'
        + '<span class="save" data-up-save>' + upsellSaveLabel(compareCents - priceCents) + '</span>'
        + '</div>'
      : '<div class="price-col" data-up-price>'
        + '<span class="pr" data-up-now>' + moneyFromCents(priceCents) + '</span>'
        + '</div>';

    var imgHtml = imgSrc
      ? '<img class="ph" src="' + imgSrc + '&width=216" alt="' + escapeHtml(title) + '" loading="lazy" width="108" height="108">'
      : '<span class="ph ph--placeholder" aria-hidden="true">🛒</span>';

    var variantsPayload = allVariants.map(function (v) {
      return {
        id: v.id,
        title: v.title || (v.options && v.options[0]) || '',
        sku: v.sku || '',
        available: v.available !== false,
        price_cents: variantPriceCents(v),
        compare_cents: variantCompareCents(v)
      };
    });

    var selHtml = '';
    if (hasMultipleVariants) {
      var curLabel = formatWeight(variant.title || (variant.options && variant.options[0]) || '');
      selHtml = ''
        + '<button type="button" class="sel sel--btn" data-up-sel-btn aria-label="' + escapeHtml(cpSelectWeight()) + '" aria-haspopup="dialog">'
        + '<span class="sel__lbl" data-up-sel-lbl>' + escapeHtml(curLabel) + '</span>'
        + '<svg class="sel__chev" viewBox="0 0 10 10" aria-hidden="true"><path fill="#7A6F61" d="M5 7L1 3h8z"/></svg>'
        + '</button>';
    } else if (hasSingleVariant) {
      var curLabel2 = formatWeight(variant.title || (variant.options && variant.options[0]) || '');
      selHtml = '<div class="sel sel--static">'
        + '<span class="sel__lbl">' + escapeHtml(curLabel2) + '</span>'
        + '</div>';
    } else {
      selHtml = '<div></div>';
    }

    var handle = product.handle || '';
    var ratingHtml = '<div class="rate" data-up-rate data-up-handle="' + escapeHtml(handle) + '" hidden></div>';

    var variantsAttr = hasWeightSel ? escapeHtml(JSON.stringify(variantsPayload)) : '';
    return ''
      + '<div class="item' + (isSale ? ' is-sale' : '') + (hasWeightSel ? ' has-sel' : '')
      + '" data-upsell-variant-id="' + variant.id + '"'
      + (hasWeightSel ? ' data-variants="' + variantsAttr + '"' : '') + '>'
      + '<a class="ph-link" href="' + url + '" tabindex="-1" aria-hidden="true">' + imgHtml + '</a>'
      + '<div class="mid">'
      + '<a class="nm" href="' + url + '">' + escapeHtml(title) + '</a>'
      + ratingHtml
      + '<div class="bottom">'
      + selHtml
      + priceHtml
      + '</div>'
      + '</div>'
      + '<button class="add" data-upsell-add type="button" aria-label="Add to cart">' + PLUS_SVG + '</button>'
      + '</div>';
  }

  function hydrateUpsellRatings(listEl) {
    if (!listEl) return;
    /* TASK-2563 — localized review word (singular/plural) emitted by Liquid
       via `| t` on the upsell host; English fallback keeps parity if absent. */
    var upHost = listEl.closest('[data-ils-cp-upsell]') || document.querySelector('[data-ils-cp-upsell]');
    var revWordOne = (upHost && upHost.getAttribute('data-review-word-one')) || 'review';
    var revWordOther = (upHost && upHost.getAttribute('data-review-word-other')) || 'reviews';
    var cards = listEl.querySelectorAll('[data-up-rate]');
    cards.forEach(function (rateEl) {
      var handle = rateEl.getAttribute('data-up-handle');
      if (!handle) return;
      var card = rateEl.closest('[data-upsell-variant-id]');
      var url = '/products/' + encodeURIComponent(handle) + '?section_id=ils-upsell-rating';
      fetch(url, { headers: { Accept: 'text/html' } })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var node = doc.querySelector('[data-ils-rating]');
          if (!node) return;
          /* TASK-2424 — persist preorder flag+date on the card so the
             on-click guard in wireUpsellAdd / addToCartFromModal can
             consult it synchronously without another round-trip. */
          if (card) {
            var poActive = node.getAttribute('data-ils-preorder-active') || '0';
            var poDate = node.getAttribute('data-ils-preorder-date') || '';
            card.setAttribute('data-ils-preorder-active', poActive);
            card.setAttribute('data-ils-preorder-date', poDate);
            /* TASK-5009 — carry the product row payload too. */
            ['product', 'image', 'url', 'variants'].forEach(function (k) {
              var poExtra = node.getAttribute('data-ils-preorder-' + k);
              if (poExtra !== null) card.setAttribute('data-ils-preorder-' + k, poExtra);
            });
            card.setAttribute('data-ils-preorder-decorated', '1');
          }
          var rating = parseFloat(node.getAttribute('data-ils-rating') || '0') || 0;
          var count = parseInt(node.getAttribute('data-ils-rating-count') || '0', 10) || 0;
          if (count <= 0) return;
          rateEl.innerHTML =
            '<span class="rate-stars" aria-hidden="true">' + starsHtml(rating) + '</span>'
            + '<span class="cnt">(' + count + ' ' + (count === 1 ? revWordOne : revWordOther) + ')</span>';
          rateEl.hidden = false;
        })
        .catch(function () {});
    });
  }

  /* TASK-2424 — Preorder guard for direct /cart/add.js calls
     (upsell «+» and weight-picker add on /cart page). Returns
     Promise<boolean>: true = proceed, false = user cancelled. */
  function checkAndConfirmPreorder(card) {
    if (!card) return Promise.resolve(true);

    function decide() {
      var a = card.getAttribute('data-ils-preorder-active');
      var d = card.getAttribute('data-ils-preorder-date') || '';
      if (a === '1' && d) {
        if (window.ILSPreorderPopup && typeof window.ILSPreorderPopup.confirm === 'function') {
          /* TASK-5009 — the popup now shows the real product row. */
          return window.ILSPreorderPopup.confirm({
            date: d,
            variantId: card.getAttribute('data-upsell-variant-id') || '',
            quantity: 1,
            title: card.getAttribute('data-ils-preorder-product') || '',
            image: card.getAttribute('data-ils-preorder-image') || '',
            url: card.getAttribute('data-ils-preorder-url') || '',
            variants: card.getAttribute('data-ils-preorder-variants') || ''
          });
        }
      }
      return Promise.resolve(true);
    }

    if (card.getAttribute('data-ils-preorder-decorated') === '1') {
      return decide();
    }

    var handleEl = card.querySelector('[data-up-handle]');
    var handle = handleEl ? handleEl.getAttribute('data-up-handle') : '';
    if (!handle) return decide();

    return fetch('/products/' + encodeURIComponent(handle) + '?section_id=ils-upsell-rating', {
      headers: { Accept: 'text/html' }
    })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('[data-ils-rating]');
        if (!node) return;
        var poActive = node.getAttribute('data-ils-preorder-active') || '0';
        var poDate = node.getAttribute('data-ils-preorder-date') || '';
        card.setAttribute('data-ils-preorder-active', poActive);
        card.setAttribute('data-ils-preorder-date', poDate);
        /* TASK-5009 — carry the product row payload too. */
        ['product', 'image', 'url', 'variants'].forEach(function (k) {
          var poExtra = node.getAttribute('data-ils-preorder-' + k);
          if (poExtra !== null) card.setAttribute('data-ils-preorder-' + k, poExtra);
        });
        card.setAttribute('data-ils-preorder-decorated', '1');
      })
      .catch(function () {})
      .then(decide);
  }

  function loadUpsell() {
    var r = root();
    if (!r) return;
    var section = r.querySelector('[data-ils-cp-upsell]');
    var list = r.querySelector('[data-ils-cp-upsell-list]');
    if (!section || !list) return;

    var cartItemEls = Array.from(r.querySelectorAll('.ils-cd__items .item[data-product-id]'));

    function readServerFallback() {
      var tag = r.querySelector('script[data-ils-cp-upsell-fallback]');
      if (!tag) return [];
      try {
        var arr = JSON.parse(tag.textContent || '[]');
        return Array.isArray(arr) ? arr : [];
      } catch (e) {
        return [];
      }
    }

    if (cartItemEls.length === 0) {
      if (list.children && list.children.length > 0) {
        section.hidden = false;
        if (list.innerHTML) lastUpsellListHtml = list.innerHTML;
        return;
      }
      if (lastUpsellListHtml) {
        list.innerHTML = lastUpsellListHtml;
        section.hidden = false;
        hydrateUpsellRatings(list);
        return;
      }
      try {
        var seed = readServerFallback();
        if (seed && seed.length > 0) {
          renderPicks(seed.slice(0, UPSELL_TARGET));
          return;
        }
      } catch (e) {}
      section.hidden = true;
      list.innerHTML = '';
      return;
    }

    var myRun = ++upsellRunId;
    var cartProductIds = new Set(cartItemEls.map(function (el) { return String(el.dataset.productId); }));
    var productIdsInOrder = cartItemEls.map(function (el) { return String(el.dataset.productId); });

    function mergeFresh(acc, seenIds, products) {
      if (!Array.isArray(products)) return acc;
      for (var i = 0; i < products.length; i++) {
        if (acc.length >= UPSELL_TARGET) break;
        var p = products[i];
        if (!p) continue;
        var id = String(p.id);
        if (cartProductIds.has(id)) continue;
        if (seenIds.has(id)) continue;
        seenIds.add(id);
        acc.push(p);
      }
      return acc;
    }

    function fetchRecs(productId, intent) {
      var url = localeRoot() + '/recommendations/products.json?product_id='
        + encodeURIComponent(productId) + '&limit=20&intent=' + intent;
      return fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (rp) { return rp.ok ? rp.json() : null; })
        .then(function (data) { return (data && Array.isArray(data.products)) ? data.products : []; })
        .catch(function () { return []; });
    }
    function fetchGeneric() {
      return fetch(localeRoot() + '/products.json?limit=50', { headers: { Accept: 'application/json' } })
        .then(function (rp) { return rp.ok ? rp.json() : null; })
        .then(function (data) { return (data && Array.isArray(data.products)) ? data.products : []; })
        .catch(function () { return []; });
    }

    function renderPicks(picks) {
      list.innerHTML = picks.map(buildUpsellCard).join('');
      section.hidden = false;
      if (list.innerHTML) lastUpsellListHtml = list.innerHTML;
      hydrateUpsellRatings(list);
    }

    if (!list.children || list.children.length === 0) {
      var seedSeen = new Set();
      var seed2 = [];
      mergeFresh(seed2, seedSeen, readServerFallback());
      if (seed2.length > 0) {
        renderPicks(seed2.slice(0, UPSELL_TARGET));
      } else if (lastUpsellListHtml) {
        list.innerHTML = lastUpsellListHtml;
        section.hidden = false;
        hydrateUpsellRatings(list);
      }
    } else {
      section.hidden = false;
      if (list.innerHTML) lastUpsellListHtml = list.innerHTML;
    }

    (function chain() {
      var picks = [];
      var seenIds = new Set();
      function step() {
        return fetchRecs(productIdsInOrder[0], 'related').then(function (got) {
          mergeFresh(picks, seenIds, got);
          if (picks.length >= UPSELL_TARGET) return;
          return fetchRecs(productIdsInOrder[0], 'complementary').then(function (got2) {
            mergeFresh(picks, seenIds, got2);
          });
        }).then(function () {
          var i = 1;
          function loop() {
            if (i >= productIdsInOrder.length || picks.length >= UPSELL_TARGET) return;
            return fetchRecs(productIdsInOrder[i], 'related').then(function (g) {
              mergeFresh(picks, seenIds, g);
              if (picks.length >= UPSELL_TARGET) return;
              return fetchRecs(productIdsInOrder[i], 'complementary').then(function (g2) {
                mergeFresh(picks, seenIds, g2);
              });
            }).then(function () { i++; return loop(); });
          }
          return loop();
        }).then(function () {
          if (picks.length >= UPSELL_TARGET) return;
          return fetchGeneric().then(function (g) { mergeFresh(picks, seenIds, g); });
        }).then(function () {
          if (picks.length >= UPSELL_TARGET) return;
          mergeFresh(picks, seenIds, readServerFallback());
        }).then(function () {
          if (myRun !== upsellRunId) return;
          if (picks.length > 0) {
            renderPicks(picks.slice(0, UPSELL_TARGET));
          } else if (list.children && list.children.length > 0) {
            section.hidden = false;
            if (list.innerHTML) lastUpsellListHtml = list.innerHTML;
          } else if (lastUpsellListHtml) {
            list.innerHTML = lastUpsellListHtml;
            section.hidden = false;
            hydrateUpsellRatings(list);
          }
        });
      }
      step();
    })();
  }

  function wireUpsellAdd() {
    var r = root();
    if (!r) return;
    r.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-upsell-add]');
      if (!btn) return;
      e.preventDefault();
      var card = btn.closest('[data-upsell-variant-id]');
      if (!card) return;
      var variantId = card.dataset.upsellVariantId;
      if (!variantId) return;
      btn.disabled = true;
      /* TASK-2424 — guard the direct /cart/add.js call behind the same
         preorder popup used by <product-form> submits (TASK-2066). */
      checkAndConfirmPreorder(card).then(function (ok) {
        if (!ok) { btn.disabled = false; return; }
        cartAddVariant(variantId, 1).then(function () { btn.disabled = false; }, function () { btn.disabled = false; });
      });
    });
  }

  /* TASK-3064 — track the wave timer at module scope and clear any prior one
     before arming a new one. The banner usually survives a morph (it sits in
     the preserved [data-ils-cp-upsell] subtree, so waveBound guards re-entry),
     but if the upsell list is momentarily empty during a morph the banner node
     is recreated and its old setInterval would keep firing on the detached node
     — a leak that compounds the animation glitch. Clearing keeps exactly one
     live timer. */
  var _cpWaveTimer = null;
  function initUpsellWave() {
    var banner = document.querySelector('[data-ils-cp-upsell-banner]');
    if (!banner || banner.dataset.waveBound) return;
    banner.dataset.waveBound = '1';
    var interval = parseInt(banner.dataset.waveInterval, 10) || 2500;
    function fire() {
      banner.classList.remove('is-waving');
      void banner.offsetWidth;
      banner.classList.add('is-waving');
    }
    if (_cpWaveTimer) { clearInterval(_cpWaveTimer); _cpWaveTimer = null; }
    fire();
    _cpWaveTimer = setInterval(fire, interval);
  }

  /* ---------- Weight popup for upsell cards ---------- */

  var weightModal = null;
  function pickAddToCartLabel() {
    var dict = { en: 'Add to cart', ru: 'Добавить', de: 'In den Warenkorb', lv: 'Pievienot grozam', et: 'Lisa korvi', lt: 'Į krepšelį' };
    var raw = '';
    try { if (typeof window.ILS_LANG === 'string' && window.ILS_LANG) raw = window.ILS_LANG; } catch (e) {}
    if (!raw) { try { raw = (window.Shopify && Shopify.locale) || ''; } catch (e) {} }
    if (!raw) raw = (document.documentElement && document.documentElement.lang) || '';
    raw = String(raw || '').toLowerCase().slice(0, 2);
    return dict[raw] || dict.en;
  }
  function pickWeightRows(n) {
    if (window.ILS && typeof window.ILS.weightRows === 'function') return window.ILS.weightRows(n);
    n = Math.max(0, parseInt(n, 10) || 0);
    if (n === 0) return [];
    if (n <= 4) return [n];
    switch (n) {
      case 5: return [3, 2];
      case 6: return [3, 3];
      case 7: return [4, 3];
      case 8: return [4, 4];
      case 9: return [3, 3, 3];
      case 10: return [4, 3, 3];
      default: {
        var rows = [], rem = n;
        while (rem > 4) { rows.push(4); rem -= 4; }
        if (rem > 0) rows.push(rem);
        return rows;
      }
    }
  }
  function ensureWeightModal() {
    if (weightModal) return weightModal;
    weightModal = document.createElement('div');
    weightModal.className = 'ils-cd__wmodal-back ils-cp__wmodal-back';
    weightModal.hidden = true;
    weightModal.setAttribute('role', 'dialog');
    weightModal.setAttribute('aria-modal', 'true');
    weightModal.setAttribute('aria-label', cpSelectWeight());
    weightModal.innerHTML =
      '<div class="ils-cd__wmodal">' +
      '<div class="ils-cd__wmodal-head">' +
      '<h3 class="ils-cd__wmodal-title">' + escapeHtml(cpSelectWeight()) + '</h3>' +
      '<button type="button" class="ils-cd__wmodal-x" aria-label="Close">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true">' +
      '<line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/>' +
      '</svg>' +
      '</button>' +
      '</div>' +
      '<div class="ils-cd__wmodal-tiles" data-ils-cp-wmodal-list></div>' +
      '<button type="button" class="ils-cd__wmodal-add" data-ils-cp-wmodal-add></button>' +
      '</div>';
    document.body.appendChild(weightModal);

    function close() {
      weightModal.classList.remove('on');
      weightModal.hidden = true;
      weightModal._card = null;
      weightModal._selectedVariantId = null;
    }
    weightModal.addEventListener('click', function (e) {
      if (e.target === weightModal) { close(); return; }
      if (e.target.closest('.ils-cd__wmodal-x')) { close(); return; }
      var opt = e.target.closest('[data-wval]');
      if (opt && weightModal._card) {
        if (opt.classList.contains('is-oos') || opt.disabled) return;
        var variantId = opt.getAttribute('data-wval');
        weightModal._selectedVariantId = variantId;
        var tiles = weightModal.querySelectorAll('.ils-cd__wmodal-tile');
        for (var i = 0; i < tiles.length; i++) {
          var on = tiles[i].getAttribute('data-wval') === String(variantId);
          tiles[i].classList.toggle('is-selected', on);
        }
        return;
      }
      var addBtn = e.target.closest('[data-ils-cp-wmodal-add]');
      if (addBtn && weightModal._card) { addToCartFromModal(); return; }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && weightModal.classList.contains('on')) close();
    });
    weightModal._close = close;
    return weightModal;
  }
  function openWeightModal(card) {
    var modal = ensureWeightModal();
    var list = modal.querySelector('[data-ils-cp-wmodal-list]');
    var variants = [];
    try { variants = JSON.parse(card.getAttribute('data-variants') || '[]'); } catch (e) { variants = []; }
    var currentId = String(card.dataset.upsellVariantId || '');
    var rows = pickWeightRows(variants.length);
    var cursor = 0;
    var html = rows.map(function (cols) {
      var inner = '';
      for (var j = 0; j < cols; j++) {
        var v = variants[cursor++];
        if (!v) break;
        var isSel = String(v.id) === currentId;
        var avail = v.available !== false;
        var label = formatWeight(v.title || (v.options && v.options[0]) || '');
        var price = moneyFromCents(parseInt(v.price_cents, 10) || 0);
        inner += ''
          + '<button type="button" class="ils-cd__wmodal-tile' + (isSel ? ' is-selected' : '') + (avail ? '' : ' is-oos') + '"'
          + ' data-wval="' + v.id + '"' + (avail ? '' : ' disabled') + '>'
          + '<span class="ils-cd__wmodal-tile-weight">' + escapeHtml(label) + '</span>'
          + '<span class="ils-cd__wmodal-tile-price">' + price + '</span>'
          + '</button>';
      }
      return '<div class="ils-cd__wmodal-row c' + cols + '">' + inner + '</div>';
    }).join('');
    list.innerHTML = html;
    modal._selectedVariantId = currentId;
    var addBtn = modal.querySelector('[data-ils-cp-wmodal-add]');
    if (addBtn) { addBtn.textContent = pickAddToCartLabel(); addBtn.disabled = false; }
    modal._card = card;
    var titleEl = modal.querySelector('.ils-cd__wmodal-title');
    if (titleEl) {
      var nm = card.querySelector('.nm');
      var name = nm ? (nm.textContent || '').trim() : '';
      titleEl.textContent = name || cpSelectWeight();
    }
    modal.hidden = false;
    modal.classList.add('on');
  }
  function applyWeightPick(card, variantId) {
    var variants = [];
    try { variants = JSON.parse(card.getAttribute('data-variants') || '[]'); } catch (e) { variants = []; }
    var v = variants.find(function (x) { return String(x.id) === String(variantId); });
    if (!v) return;
    var priceCents = parseInt(v.price_cents, 10) || 0;
    var compareCents = parseInt(v.compare_cents, 10) || 0;
    var isSale = compareCents > 0;
    card.dataset.upsellVariantId = v.id;
    var priceColEl = card.querySelector('[data-up-price]');
    if (priceColEl) {
      priceColEl.innerHTML = isSale
        ? '<span class="pr-old" data-up-old>' + moneyFromCents(compareCents) + '</span>'
          + '<span class="pr" data-up-now>' + moneyFromCents(priceCents) + '</span>'
          + '<span class="save" data-up-save>' + upsellSaveLabel(compareCents - priceCents) + '</span>'
        : '<span class="pr" data-up-now>' + moneyFromCents(priceCents) + '</span>';
    }
    card.classList.toggle('is-sale', isSale);
    var btnLbl = card.querySelector('[data-up-sel-lbl]');
    if (btnLbl) btnLbl.textContent = formatWeight(v.title || (v.options && v.options[0]) || '');
  }
  function addToCartFromModal() {
    if (!weightModal || !weightModal._card) return;
    var card = weightModal._card;
    var variantId = weightModal._selectedVariantId || card.dataset.upsellVariantId;
    if (!variantId) return;
    applyWeightPick(card, variantId);
    var addBtn = weightModal.querySelector('[data-ils-cp-wmodal-add]');
    if (addBtn) addBtn.disabled = true;
    /* TASK-2424 — guard the direct /cart/add.js call behind the preorder
       popup (same as the upsell «+» button path above). */
    checkAndConfirmPreorder(card).then(function (ok) {
      if (!ok) {
        if (addBtn) addBtn.disabled = false;
        if (weightModal && weightModal._close) weightModal._close();
        return;
      }
      cartAddVariant(variantId, 1).then(function () {
        if (addBtn) addBtn.disabled = false;
        if (weightModal && weightModal._close) weightModal._close();
      }, function () {
        if (addBtn) addBtn.disabled = false;
        if (weightModal && weightModal._close) weightModal._close();
      });
    });
  }
  function wireWeightPopup() {
    var r = root();
    if (!r) return;
    r.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-up-sel-btn]');
      if (!btn) return;
      e.preventDefault();
      var card = btn.closest('[data-upsell-variant-id]');
      if (!card) return;
      btn.blur();
      openWeightModal(card);
    });
  }

  /* ---------- Leader row (page-scoped hydration + confetti) ---------- */

  function parseLeaderEur(raw) {
    if (!raw) return 0;
    var stripped = String(raw).replace(/[^\d.,-]/g, '');
    if (!stripped) return 0;
    var normalized;
    if (stripped.indexOf(',') !== -1 && stripped.indexOf('.') !== -1) {
      normalized = stripped.replace(/\./g, '').replace(',', '.');
    } else if (stripped.indexOf(',') !== -1) {
      normalized = stripped.replace(',', '.');
    } else if (stripped.indexOf('.') !== -1) {
      /* TASK-5946 — '.' alone is a THOUSANDS separator in all 7 presentment
         formats this shop serves (REPORT-5944 B.2: the decimal is always ',').
         Before the zero-fraction trim this branch only saw HUF ("5.000 Ft",
         already misread as 5); after it, it also sees "€1.301". */
      normalized = stripped.replace(/\./g, '');
    } else {
      normalized = stripped;
    }
    var n = parseFloat(normalized);
    return isFinite(n) && n > 0 ? n : 0;
  }

  function hydratePageLeader() {
    var row = document.querySelector('[data-ils-cp-leader-row]');
    var amt = document.querySelector('[data-ils-cp-leader]');
    if (!row || !amt) return;
    var value = (row.getAttribute('data-ils-cp-leader-original') || '').trim();
    if (!value) value = (amt.textContent || '').trim();
    var liveCached = window.ILS && window.ILS._leaderLiveValue;
    if (liveCached) {
      value = liveCached;
      row.setAttribute('data-ils-cp-leader-original', liveCached);
    }
    if (!value) { row.hidden = true; return; }
    amt.textContent = value;
    row.hidden = false;
  }

  /* TASK-3061 — localized leader label. Same source + fallback pattern as
     cpSelectWeight() above: read the translated leaderLabel / leaderLabelYou
     from the data-ils-rw-i18n JSON block (emitted by custom-cart-rewards.liquid,
     rendered on this page via custom-main-cart). Before this, renderPageLeaderState()
     overwrote the server-rendered `custom.cart.leader_label` translation with a
     hardcoded English string right after load, so every locale saw English. */
  var _cpLeaderLblCache = null;
  function cpLeaderLabels() {
    if (_cpLeaderLblCache) return _cpLeaderLblCache;
    var d = { leaderLabel: 'This Week Current Leader:', leaderLabelYou: 'YOU are This Week Current Leader:' };
    try {
      var el = document.querySelector('[data-ils-rw-i18n]');
      if (el && el.textContent) {
        var p = JSON.parse(el.textContent);
        if (p && (p.leaderLabel || p.leaderLabelYou)) {
          if (p.leaderLabel) d.leaderLabel = p.leaderLabel;
          if (p.leaderLabelYou) d.leaderLabelYou = p.leaderLabelYou;
          _cpLeaderLblCache = d;
        }
      }
    } catch (e) {}
    return d;
  }

  /* TASK-5795 — the cart's value in EUR, for the leader comparison below.
     The legacy path parsed the RENDERED subtotal, so on /en-se/ "1 100,00 kr"
     became the number 1100 and beat the EUR 294,76 leader: the page told a
     Swedish shopper with ~EUR 97 in the basket that they were this week's
     leader, and offered the EUR 20 bonus that goes with it. Measured live on
     2026-10-24 at 1100 SEK, 530 RON and 5000 HUF.
     The presentment cents come from the rewards panel's own data-cart-total
     (emitted by snippets/custom-cart-rewards.liquid and kept current by
     custom-cart-drawer.js), so no money string has to be re-parsed at all.
     null = refuse to decide; the caller then leaves the leader row alone. */
  function cpCartEur() {
    if (window.ILSMoney && window.ILSMoney.on()) {
      var panel = document.querySelector('[data-ils-rewards]');
      var cents = panel ? parseInt(panel.getAttribute('data-cart-total') || '', 10) : NaN;
      if (!isFinite(cents)) return null;
      return window.ILSMoney.eurFromCents(cents);
    }
    var subtotalEl = document.querySelector('[data-ils-cp-subtotal]');
    return subtotalEl ? parseLeaderEur(subtotalEl.textContent) : 0;
  }

  function renderPageLeaderState() {
    var row = document.querySelector('[data-ils-cp-leader-row]');
    var lbl = document.querySelector('[data-ils-cp-leader-lbl]');
    var amt = document.querySelector('[data-ils-cp-leader]');
    if (!row || !lbl || !amt) return;
    var cartEur = cpCartEur();                                   // TASK-5795
    var liveCached = window.ILS && window.ILS._leaderLiveValue;
    var original = liveCached || row.getAttribute('data-ils-cp-leader-original') || '';
    /* TASK-5856 — REPORT-5848 D5: `original` is now a LOCALISED string on the
       six ("1 100,05 kr"), so parseLeaderEur() would read 1100,05 as euros and
       hand this comparison a presentment number. The painter publishes the raw
       EUR value; prefer it, and keep the parse for a cached page that predates
       the channel. The DISPLAYED string stays `original` either way. */
    var _le5856 = window.ILS && window.ILS._leaderEurValue;
    var leaderEur = (typeof _le5856 === 'number' && isFinite(_le5856) && _le5856 > 0)
      ? _le5856
      : parseLeaderEur(original);
    if (cartEur === null) {                                      // TASK-5795
      lbl.textContent = cpLeaderLabels().leaderLabel;
      if (original) amt.textContent = original;
      return;
    }
    if (leaderEur > 0 && cartEur > leaderEur) {
      lbl.textContent = cpLeaderLabels().leaderLabelYou;
      /* TASK-5856 — REPORT-5848 D8, the /cart mirror of the drawer's fix. The
         shopper's own basket is a fact, not a target, so it is formatted in the
         presentment currency without being pushed up a rounding grid. Falls back
         to the previous euro string on EUR markets and whenever the oracle
         refuses. */
      var _m5856 = window.ILSRewardMatrix;
      var _s5856 = null;
      if (_m5856 && typeof _m5856.eurMoney === 'function') {
        try { _s5856 = _m5856.eurMoney(cartEur); } catch (e) { _s5856 = null; }
      }
      amt.textContent = (_s5856 !== null) ? _s5856 : ('€' + money2(cartEur));
    } else {
      lbl.textContent = cpLeaderLabels().leaderLabel;
      if (original) amt.textContent = original;
    }
  }

  /* ---------- Payment-icons marquee (page-scoped port of the drawer's
     initCdPayMarquee — TASK-3034) ----------

     The /cart footer reuses the drawer's `.ils-cd__pay-*` markup + CSS but
     with PAGE data attributes (`data-ils-cp-pay-marquee` / `-track`). The
     drawer's own initCdPayMarquee only selects `data-ils-cd-*`, so the page
     strip was never animated and sat static. This is a faithful port scoped
     to the page attributes. To avoid colliding with the drawer's shared
     keyframe (the drawer is also on this page, hidden), it uses a DISTINCT
     keyframe name (ilsCpPayMarqueeRun) + <style> id. See TASK-2876 in
     custom-cart-drawer.js for the concrete-px-keyframe rationale. */
  function initCpPayMarquee() {
    var wrap = document.querySelector('[data-ils-cp-pay-marquee]');
    var track = document.querySelector('[data-ils-cp-pay-track]');
    if (!wrap || !track) return;

    var clones = track.querySelectorAll('.ils-cd__pay-set[data-ils-cp-pay-clone]');
    var originals = track.querySelectorAll('.ils-cd__pay-set:not([data-ils-cp-pay-clone])');
    var firstSet = originals[0];
    var secondSet = originals[1];
    if (!firstSet) return;

    var setW = firstSet.getBoundingClientRect().width;
    var wrapW = wrap.clientWidth;
    if (!(setW > 0) || !(wrapW > 0)) return;

    /* Measure the exact one-set repeat period (set width + one inter-set gap)
       as the x-distance between the first chip of set #1 and set #2, matching
       the drawer's TASK-2670 seam measurement (per-chip margins aren't
       uniform, so setW + fixed-gap would drift). */
    var seamC1 = firstSet.querySelector('.sa-pay-chip');
    var seamC2 = secondSet && secondSet.querySelector('.sa-pay-chip');
    var shift;
    if (seamC1 && seamC2) {
      shift = seamC2.getBoundingClientRect().left - seamC1.getBoundingClientRect().left;
    } else {
      shift = setW + 10;
    }
    if (!(shift > 0)) return;

    var needed = Math.max(originals.length, Math.ceil(wrapW / setW) + 2);

    /* Idempotency guard (TASK-2876 class of bug) — postMorphInit + resize can
       call this repeatedly; bail without touching the DOM/animation when the
       geometry is unchanged so the running belt isn't restarted every pass. */
    var sig = needed + '|' + shift.toFixed(2) + '|' + Math.round(wrapW);
    if (wrap.classList.contains('is-mq')
        && track.__ilsCpPayMqSig === sig
        && clones.length === (needed - originals.length)
        && track.style.animationName === 'ilsCpPayMarqueeRun') {
      return;
    }
    track.__ilsCpPayMqSig = sig;

    clones.forEach(function (n) { n.remove(); });
    for (var i = originals.length; i < needed; i++) {
      var clone = firstSet.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-ils-cp-pay-clone', '');
      track.appendChild(clone);
    }

    var ILS_PAY_MQ_SPEED_PX_PER_SEC = 25;
    var duration = shift / ILS_PAY_MQ_SPEED_PX_PER_SEC;

    /* Concrete-px keyframe (unregistered-var endpoints don't interpolate in
       Chrome/Safari — TASK-2876). Distinct name from the drawer's so the two
       marquees on this page don't overwrite each other's keyframe. */
    var kfStyle = document.getElementById('ils-cp-pay-mq-kf');
    if (!kfStyle) {
      kfStyle = document.createElement('style');
      kfStyle.id = 'ils-cp-pay-mq-kf';
      document.head.appendChild(kfStyle);
    }
    kfStyle.textContent =
      '@keyframes ilsCpPayMarqueeRun{from{transform:translateX(0)}' +
      'to{transform:translateX(-' + shift.toFixed(2) + 'px)}}';

    track.style.setProperty('--ils-cd-pay-shift', '-' + shift.toFixed(2) + 'px');
    wrap.style.setProperty('--ils-cd-pay-mq-duration', duration.toFixed(2) + 's');
    wrap.classList.add('is-mq');
    track.style.animationName = 'ilsCpPayMarqueeRun';
  }

  function wireCpPayMarqueeResize() {
    if (window.__ilsCpPayResizeBound) return;
    window.__ilsCpPayResizeBound = true;
    var rafToken = null;
    window.addEventListener('resize', function () {
      if (rafToken) cancelAnimationFrame(rafToken);
      rafToken = requestAnimationFrame(initCpPayMarquee);
    });
  }

  /* ---------- Fixed mobile footer bottom-padding (TASK-3045) ----------
     On mobile (< 990px) the totals / CTA / payment footer
     (`.ils-cp__col--side`) is pinned to the viewport bottom
     (position:fixed — see custom-cart-page.css), mirroring the drawer
     where `.ils-cd__foot` stays glued below the scrolling `.ils-cd__body`.
     To keep the last item / upsell card fully reachable when scrolled to
     the very end, the main column reserves a bottom padding equal to the
     footer's height. That height is variable (timer, subtotal, language),
     so we measure it and expose it as `--ils-cp-foot-h`; the CSS carries a
     static fallback for first paint. Desktop (>= 990px) ignores the var. */
  var footMq = window.matchMedia('(max-width: 989.98px)');
  var footPadObserver = null;

  function syncFootPadding() {
    var rootEl = root();
    if (!rootEl) return;
    var side = rootEl.querySelector('.ils-cp__col--side');
    if (!side || !footMq.matches) {
      rootEl.style.removeProperty('--ils-cp-foot-h');
      document.body.style.removeProperty('padding-bottom'); // TASK-3047 — drop the page-bottom reserve off mobile / empty cart
      return;
    }
    var h = side.offsetHeight;
    if (h > 0) {
      /* TASK-3047 (point 1) — reserve the footer's EXACT height (was h + 12).
         The upsell strip already carries a 12px bottom gap (list 4px +
         .ils-cp__upsell 8px = the drawer's last-card -> foot gap), so the old
         +12 stacked on top of it and produced ~24px of dead air before the
         pinned footer. Reserving exactly h lets the upsell provide the small
         12px gap, matching the drawer. */
      rootEl.style.setProperty('--ils-cp-foot-h', h + 'px');
      /* TASK-3047 (point 2) — the pinned footer (.ils-cp__col--side,
         position:fixed) overlays the WHOLE page, but the reserve above only
         lifts content INSIDE the cart section. The theme's site footer (logo +
         country list + socials) sits AFTER the cart section, so its bottom h px
         was clipped behind the fixed bar at max scroll. Reserve the same height
         at the page bottom so the entire site footer clears the pinned bar.
         Scoped to the cart page (this script only runs where root() exists) and
         cleaned up above on desktop / empty cart. */
      document.body.style.paddingBottom = h + 'px';
    }
  }

  function observeFoot() {
    if (typeof ResizeObserver !== 'function') { syncFootPadding(); return; }
    var rootEl = root();
    var side = rootEl && rootEl.querySelector('.ils-cp__col--side');
    if (footPadObserver) { try { footPadObserver.disconnect(); } catch (e) {} }
    if (!side) return;
    footPadObserver = new ResizeObserver(function () { syncFootPadding(); });
    try { footPadObserver.observe(side); } catch (e) {}
  }

  function wireFootPadding() {
    if (window.__ilsCpFootPadBound) return;
    window.__ilsCpFootPadBound = true;
    var t = null;
    window.addEventListener('resize', function () {
      if (t) cancelAnimationFrame(t);
      t = requestAnimationFrame(syncFootPadding);
    });
    // Re-measure when crossing the desktop/mobile breakpoint.
    try {
      if (typeof footMq.addEventListener === 'function') footMq.addEventListener('change', syncFootPadding);
      else if (typeof footMq.addListener === 'function') footMq.addListener(syncFootPadding);
    } catch (e) {}
  }

  /* ---------- Init / re-init ---------- */

  function postMorphInit() {
    // Called after morphSelfSection replaces .ils-cp innerHTML.
    hydratePageLeader();
    renderPageLeaderState();
    applyStepperMinusVisibility(root());
    loadUpsell();
    initUpsellWave();
    initCpPayMarquee(); // TASK-3034 — re-arm the pay marquee after section morph
    syncFootPadding();  // TASK-3045 — re-measure the fixed footer height for the content bottom-pad
    observeFoot();      // TASK-3045 — re-observe the freshly-morphed footer node
    // Rewards + stim/pop are painted by custom-cart-drawer.js's global
    // querySelectorAll — a stale total_price on this page's morph payload
    // is fine because the drawer JS reads `data-cart-total` off the
    // freshly-rendered rewards panel. Trigger it manually just in case
    // the drawer hasn't received the pub/sub yet.
    try {
      // Let the drawer JS re-hydrate rewards from the freshly-rendered
      // data-cart-total attribute.
      var panels = document.querySelectorAll('[data-ils-rewards]');
      if (panels.length && window.dispatchEvent) {
        /* TASK-3064 — INTERNAL marker. Without it this post-morph ping was
           caught by our own cart:update listener → scheduled another
           fetchAndMorphSelf() → whose postMorphInit fired cart:update again …
           an INFINITE self-morph loop (~120ms cycle) that never settled until
           F5. That perpetual re-morph re-initialised the upsell finger-wave and
           the payment marquee every cycle (the "glitch") and kept the remove
           modal permanently stale. The drawer still repaints rewards off this
           event; only our own morph re-trigger is suppressed. */
        document.dispatchEvent(new CustomEvent('cart:update', { detail: { ilsCpInternal: true } }));
      }
    } catch (e) {}
  }

  function init() {
    if (!root()) return;
    wireRemoveConfirm();
    wireKeyBasedQty();
    wireStepperMinusVisibility();
    wireContinueShopping();
    wireCheckoutGate();       // TASK-3340 — Checkout → /pages/delivery-selector
    wireUpsellAdd();
    wireWeightPopup();

    hydratePageLeader();
    renderPageLeaderState();
    document.addEventListener('ils:leader-live-ready', function () {
      hydratePageLeader();
      renderPageLeaderState();
    });

    loadUpsell();
    initUpsellWave();
    initCpPayMarquee();       // TASK-3034 — animate the /cart pay-icons strip
    wireCpPayMarqueeResize(); // re-measure on viewport resize
    syncFootPadding();        // TASK-3045 — reserve bottom space for the fixed mobile footer
    observeFoot();            // TASK-3045 — track footer height changes
    wireFootPadding();        // TASK-3045 — re-measure on resize / breakpoint change

    // Keep our copy in sync when Dawn (or the drawer) publishes cart updates.
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
          if (event && event.source === 'ils-cart-page') return; // our own event, morph already ran
          if (event && event.source === 'ils-cart-page-upsell') return;
          fetchAndMorphSelf();
        });
      }
    } catch (e) {}
    document.addEventListener('cart:update', function (e) {
      /* TASK-3064 — ignore cart:update events WE dispatched (refreshDrawerSection
         + postMorphInit). They carry detail.ilsCpInternal and must NOT re-trigger
         a morph: cartChangeByKey / cartAddVariant already morph once, and the
         post-morph ping re-firing this handler was the infinite-loop engine.
         External cart:update (Dawn, the drawer) is unmarked → still morphs. */
      if (e && e.detail && e.detail.ilsCpInternal) return;
      // Debounce to avoid repeated identical fetches.
      if (init._morphPending) return;
      init._morphPending = true;
      setTimeout(function () {
        init._morphPending = false;
        fetchAndMorphSelf();
      }, 120);
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
